import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { localizeAssignmentTitle } from '@/lib/i18nTitles';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SuiteIcon } from '@/components/shared/SuiteIcon';
import {
  ArrowRight, Calendar, CheckCircle2, Loader2, Briefcase, Inbox, Sparkles,
  ClipboardCheck, ListChecks, Activity, Target,
} from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { MyTraining } from '@/components/enduser/MyTraining';
import MeOnboardingCard from '@/components/enduser/MeOnboardingCard';
import MeEvaluatorTasksCard from '@/components/enduser/MeEvaluatorTasksCard';
import MeRecognitionsCard from '@/components/enduser/MeRecognitionsCard';
import MeCompanyRecognitionFeed from '@/components/enduser/MeCompanyRecognitionFeed';
import MeSurveysCard from '@/components/enduser/MeSurveysCard';
import { MeWrEntryCard } from '@/components/enduser/MeWrEntryCard';
import ApplicationCard from '@/components/enduser/ApplicationCard';

/**
 * Sprint TC-B.2 — "My TalentCheck" candidate landing page.
 *
 * Replaces the bare assessments list with a real candidate dashboard that
 * groups assessments by the recruitment application they belong to. Standalone
 * (non-recruitment) assessments and the existing TalentHeart surfaces
 * (onboarding, recognitions, surveys) are preserved underneath.
 *
 * Backed by `GET /api/me/dashboard`.
 */

import { bizDate } from '@/lib/dates';

// TP-UAT (D): business dates must never shift by timezone — delegate to bizDate.
const fmt = (iso, lang) => bizDate(iso, lang);
const _legacyFmt = (iso, lang) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(
      lang?.startsWith('es') ? 'es-ES' : 'en-US',
      { month: 'short', day: 'numeric', year: 'numeric' },
    );
  } catch { return iso; }
};

const STATUS_TONE = {
  requested:   { bg: 'bg-info/10',    text: 'text-info',    border: 'border-info/30' },
  in_progress: { bg: 'bg-warning/10', text: 'text-warning', border: 'border-warning/30' },
  submitted:   { bg: 'bg-success/10', text: 'text-success', border: 'border-success/30' },
};

function KpiCard({ icon: Icon, label, value, hint, accent = 'primary', testId }) {
  const accents = {
    primary: 'bg-primary/10 text-primary',
    info:    'bg-info/10 text-info',
    warning: 'bg-warning/10 text-warning',
    success: 'bg-success/10 text-success',
  };
  return (
    <Card className="p-4 shadow-card flex items-center gap-3" data-testid={testId}>
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${accents[accent]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] sm:text-xs uppercase tracking-wide text-muted-foreground break-normal leading-tight">{label}</p>
        <p className="text-2xl font-bold tabular-nums leading-tight">{value}</p>
        {hint && <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{hint}</p>}
      </div>
    </Card>
  );
}

export default function EvaluatedHome() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useApp();
  const isES = (i18n.language || '').startsWith('es');
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/me/dashboard');
        if (alive) setDash(data);
      } catch (e) {
        toast.error(
          isES ? 'No se pudo cargar tu panel' : 'Could not load your dashboard',
          { description: formatApiError(e) },
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const firstName = (user?.name || '').split(' ')[0] || (isES ? 'allí' : 'there');
  // R2 — "Tus postulaciones" is a Recruitment (TalentCheck) concept; hide it
  // for companies without the recruitment suite enabled.
  const hasRecruitment = (user?.company_suite_codes || []).includes('talentcheck');

  return (
    <div className="space-y-6" data-testid="evaluated-home">
      <MyTraining />
      {/* ── Hero ────────────────────────────────────────────────── */}
      <header data-testid="dashboard-hero">
        <Badge variant="outline" className="text-xs uppercase tracking-wider gap-1.5 mb-2">
          <Sparkles className="h-3 w-3" /> {isES ? 'Mi Portal' : 'My Portal'}
        </Badge>
        <h1 className="font-display text-3xl font-bold tracking-tight">
          {isES ? `Hola ${firstName}` : `Hi ${firstName}`}
        </h1>
        <p className="text-base text-muted-foreground mt-1">
          {hasRecruitment
            ? (isES
              ? 'Este es tu panel personal: aquí ves tus postulaciones activas, evaluaciones pendientes y el estado de tu proceso.'
              : 'This is your personal cockpit: active applications, pending assessments, and your hiring journey at a glance.')
            : (isES
              ? 'Este es tu panel personal: aquí ves tus evaluaciones pendientes y el estado de tu proceso.'
              : 'This is your personal cockpit: pending assessments and your progress at a glance.')}
        </p>
      </header>

      {loading ? (
        <Card className="p-10 shadow-card flex items-center justify-center text-muted-foreground" data-testid="dashboard-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          {isES ? 'Cargando tu panel…' : 'Loading your dashboard…'}
        </Card>
      ) : (
        <>
          {/* ── KPIs ─────────────────────────────────────────── */}
          {dash && (
            <div className={`grid grid-cols-2 ${hasRecruitment ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-3`} data-testid="dashboard-kpis">
              {hasRecruitment && (
                <KpiCard
                  icon={Briefcase}
                  label={t('evaluatedHome.kpi.activeApplications', { defaultValue: isES ? 'Postulaciones activas' : 'Active applications' })}
                  value={dash.kpis.active_applications}
                  accent="primary"
                  testId="kpi-active-applications"
                />
              )}
              <KpiCard
                icon={Inbox}
                label={t('evaluatedHome.kpi.pendingAssessments', { defaultValue: isES ? 'Evaluaciones pendientes' : 'Pending assessments' })}
                value={dash.kpis.pending_assessments}
                accent="warning"
                testId="kpi-pending-assessments"
              />
              <KpiCard
                icon={ClipboardCheck}
                label={isES ? 'Enviadas' : 'Submitted'}
                value={dash.kpis.submitted_assessments}
                accent="success"
                testId="kpi-submitted-assessments"
              />
              <KpiCard
                icon={Activity}
                label={t('evaluatedHome.kpi.avgProgress', { defaultValue: isES ? 'Progreso de evaluaciones' : 'Assessment Progress' })}
                value={`${dash.kpis.average_progress}%`}
                hint={t('evaluatedHome.kpi.avgProgressHint', { defaultValue: isES ? 'Porcentaje de evaluaciones asignadas que han sido completadas.' : 'Percentage of assigned assessments that have been completed.' })}
                accent="info"
                testId="kpi-average-progress"
              />
            </div>
          )}

          {/* ── Applications (Recruitment/TalentCheck only) ────── */}
          {hasRecruitment && (
          <section data-testid="dashboard-applications">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-primary" />
                {isES ? 'Tus postulaciones' : 'Your applications'}
              </h2>
              {dash?.applications?.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {dash.applications.length} {t('evaluatedHome.totalIncludesClosed', { defaultValue: isES ? 'en total (incluye cerradas)' : 'in total (includes closed)' })}
                </span>
              )}
            </div>
            {(!dash?.applications || dash.applications.length === 0) ? (
              <Card className="p-8 text-center shadow-card" data-testid="dashboard-no-applications">
                <Briefcase className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-base text-muted-foreground">
                  {isES
                    ? 'Aún no hay postulaciones activas asociadas a tu perfil. Cuando un cliente te considere para una posición, aparecerá aquí.'
                    : 'No active applications yet. When a client invites you to a position you will see it here.'}
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {dash.applications.map((a) => (
                  <ApplicationCard key={a.process_id} app={a} />
                ))}
              </div>
            )}
          </section>
          )}

          {/* ── TalentCompetency engagements (Sprint TCO-C) ──────────────── */}
          {dash?.talentcompetency_engagements?.length > 0 && (
            <section data-testid="dashboard-talentcompetency">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  {isES ? 'Mis evaluaciones TalentCompetency' : 'My TalentCompetency engagements'}
                </h2>
                <span className="text-sm text-muted-foreground">
                  {dash.talentcompetency_engagements.length} {isES ? 'en total' : 'in total'}
                </span>
              </div>
              <div className="space-y-4">
                {dash.talentcompetency_engagements.map((e) => {
                  const tone = e.application_status === 'submitted'
                    ? STATUS_TONE.submitted
                    : e.application_status === 'in_progress'
                      ? STATUS_TONE.in_progress
                      : STATUS_TONE.requested;
                  return (
                    <Card
                      key={e.request_id}
                      className="p-5 shadow-card"
                      data-testid={`tco-engagement-card-${e.request_id}`}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <Target className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-base truncate" data-testid={`tco-engagement-profile-${e.request_id}`}>
                              {e.profile_name || (isES ? 'Perfil de competencia' : 'Competency profile')}
                            </p>
                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                              {e.company_name && <span>{e.company_name}</span>}
                              {e.position && <span>· {e.position}</span>}
                              {e.department && <span>· {e.department}</span>}
                            </div>
                          </div>
                        </div>
                        <Badge variant="outline" className={`${tone.bg} ${tone.text} ${tone.border}`} data-testid={`tco-engagement-status-${e.request_id}`}>
                          {e.application_status === 'invited'
                            ? (isES ? 'Pendiente' : 'Pending')
                            : e.application_status === 'in_progress'
                              ? (isES ? 'En curso' : 'In progress')
                              : (isES ? 'Enviada' : 'Submitted')}
                        </Badge>
                      </div>

                      <div className="text-xs text-muted-foreground mt-3 flex items-center gap-3 flex-wrap">
                        {e.requested_by_name && (
                          <span>{isES ? 'Solicitado por' : 'Requested by'}: {e.requested_by_name}</span>
                        )}
                        {e.closing_date && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {isES ? 'Fecha límite' : 'Due by'}: {fmt(e.closing_date, i18n.language)}
                          </span>
                        )}
                        <span>
                          {e.assessments_completed}/{e.assessments_total} {isES ? 'evaluaciones' : 'assessments'}
                        </span>
                      </div>

                      {/* Per-assignment list (one row per linked questionnaire). */}
                      <div className="mt-4 divide-y border rounded-md" data-testid={`tco-engagement-assignments-${e.request_id}`}>
                        {e.assignments.map((a) => {
                          const submitted = a.status === 'submitted';
                          return (
                            <div
                              key={a.id}
                              className="flex items-center justify-between px-3 py-2.5 gap-2"
                              data-testid={`tco-engagement-assignment-${a.id}`}
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-base font-medium truncate" title={localizeAssignmentTitle(a.title, i18n.language)}>{localizeAssignmentTitle(a.title, i18n.language)}</p>
                                {a.deadline && (
                                  <p className="text-xs text-muted-foreground">
                                    {isES ? 'Fecha límite' : 'Due by'}: {fmt(a.deadline, i18n.language)}
                                  </p>
                                )}
                              </div>
                              {submitted ? (
                                <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/30">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  {isES ? 'Enviada' : 'Submitted'}
                                </Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  className="gap-1.5"
                                  onClick={() => navigate(`/me/assignments/${a.id}`)}
                                  data-testid={`tco-engagement-start-${a.id}`}
                                >
                                  {a.status === 'in_progress'
                                    ? (isES ? 'Continuar' : 'Continue')
                                    : (isES ? 'Comenzar' : 'Start')}
                                  <ArrowRight className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {e.application_status === 'submitted' && (
                        <div className="mt-3 text-xs text-muted-foreground italic" data-testid={`tco-engagement-awaiting-${e.request_id}`}>
                          {isES
                            ? 'Evaluación enviada. A la espera de la evaluación de CTH.'
                            : 'Submitted successfully. Awaiting CTH evaluation.'}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </section>
          )}

          {/* ── Standalone assessments (non-recruitment) ────── */}
          {dash?.standalone_assignments?.length > 0 && (
            <section data-testid="dashboard-standalone">
              <h2 className="font-display text-lg font-bold tracking-tight mb-3 flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-primary" />
                {isES ? 'Otras evaluaciones' : 'Other evaluations'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="standalone-grid">
                {dash.standalone_assignments.map((a) => {
                  const submitted = a.status === 'submitted';
                  const tone = STATUS_TONE[a.status] || STATUS_TONE.requested;
                  return (
                    <Card
                      key={a.id}
                      className={`p-5 shadow-card transition-smooth min-w-0 ${
                        submitted
                          ? 'opacity-70'
                          : 'hover:shadow-elegant hover:-translate-y-0.5 cursor-pointer'
                      }`}
                      onClick={() => !submitted && navigate(`/me/assignments/${a.id}`)}
                      data-testid={`standalone-card-${a.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <SuiteIcon name={a.suite?.icon} color={a.suite?.color} size="md" />
                          <div className="min-w-0">
                            <p className="font-semibold text-base truncate" title={localizeAssignmentTitle(a.title, i18n.language)}>{localizeAssignmentTitle(a.title, i18n.language)}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {a.suite?.name || a.suite_code}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className={`${tone.bg} ${tone.text} ${tone.border}`}>
                          {a.status === 'requested'
                            ? (isES ? 'Pendiente' : 'Pending')
                            : a.status === 'in_progress'
                            ? (isES ? 'En curso' : 'In progress')
                            : a.status === 'submitted'
                            ? (isES ? 'Enviada' : 'Submitted')
                            : t(`recruitment.candidate.status.${a.status}`, { defaultValue: a.status })}
                        </Badge>
                      </div>
                      <div className="mt-4 flex items-center text-xs text-muted-foreground gap-2">
                        <Calendar className="h-3.5 w-3.5" />
                        {a.deadline
                          ? `${isES ? 'Fecha límite' : 'Due by'}: ${fmt(a.deadline, i18n.language)}`
                          : (isES ? 'Sin fecha límite' : 'No deadline')}
                        {a.tp_context && (
                          <span className="mt-1 block rounded border border-primary/30 bg-primary/5 px-2 py-1 text-xs text-primary" data-testid={`tp-eval-subject-${a.id}`}>
                            {isES ? 'Evaluando a' : 'Evaluating'}: <b>{a.tp_context.subject_name}</b>
                            {a.tp_context.subject_position ? ` · ${a.tp_context.subject_position}` : ''}
                            {a.tp_context.subject_department ? ` · ${a.tp_context.subject_department}` : ''}
                            {' · '}{isES
                              ? (a.tp_context.evaluator_role === 'supervisor' ? 'Rol: Supervisor/a' : 'Rol: Cliente Interno')
                              : (a.tp_context.evaluator_role === 'supervisor' ? 'Role: Supervisor' : 'Role: Internal Client')}
                          </span>
                        )}
                      </div>
                      {!submitted && (
                        <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-end">
                          <Button size="sm" className="gap-1.5" data-testid={`standalone-start-${a.id}`}>
                            {a.status === 'in_progress'
                              ? (isES ? 'Continuar' : 'Continue')
                              : (isES ? 'Comenzar' : 'Start')}
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </section>
          )}

          {/* CTH-015 — Mi Empresa: communications + celebrations hub */}
          {!user?.external_candidate && (
            <Card className="p-4 shadow-card border-teal-200 bg-gradient-to-r from-teal-50 to-white cursor-pointer hover:shadow-medium transition-smooth"
                  onClick={() => navigate('/me/company')} data-testid="me-company-link">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-sm">{t('me.companyHub.title', { defaultValue: 'Mi Empresa' })}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('me.companyHub.subtitle', { defaultValue: 'Comunicaciones, celebraciones y reconocimientos de tu organización' })}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="rounded-full shrink-0" data-testid="me-company-link-btn">
                  {t('me.companyHub.open', { defaultValue: 'Abrir' })}
                </Button>
              </div>
            </Card>
          )}
          {/* ── TalentHeart surfaces preserved ───────────────── */}
          <MeEvaluatorTasksCard />
          <MeOnboardingCard />
          <MeWrEntryCard />
          {/* P0 UAT 2026-08-11 — Recognition is employee-only: the external-
              candidate portal composition excludes it entirely. */}
          {!user?.external_candidate && <MeRecognitionsCard />}
          {/* Founder UAT 2026-06 — company-wide PUBLIC recognition feed */}
          {!user?.external_candidate && <MeCompanyRecognitionFeed />}
          <MeSurveysCard />

          {/* Empty-state fallback when literally nothing exists */}
          {dash &&
            !dash.applications?.length &&
            !dash.talentcompetency_engagements?.length &&
            !dash.standalone_assignments?.length && (
              <Card className="p-10 text-center shadow-card" data-testid="dashboard-empty">
                <CheckCircle2 className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-base text-muted-foreground">
                  {isES
                    ? 'No tienes nada pendiente en este momento. Disfruta del día.'
                    : "You're all caught up. Enjoy your day!"}
                </p>
              </Card>
            )}
        </>
      )}
    </div>
  );
}
